// Navigation CMS client
// Fetches nav config from Supabase via /api/cms/navigation.
// Caches in localStorage for 5 minutes. Falls back to FALLBACK_NAV on any error.

const API_BASE = '';
const CACHE_KEY = 'gfs_nav_cache_v1';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface NavItem {
  id: string;
  label: string;
  route: string;
  header_visible: boolean;
  more_visible: boolean;
  footer_visible: boolean;
  footer_group: string | null;
  footer_group_order: number;
  sort_order: number;
  footer_sort_order: number;
  open_new_tab: boolean;
  active: boolean;
  is_cta: boolean;
}

interface NavCache {
  fetchedAt: number;
  items: NavItem[];
}

// ── Hardcoded fallback ────────────────────────────────────────────────────────
// Used when Supabase is unavailable. Mirrors the migration seed data exactly.
export const FALLBACK_NAV: NavItem[] = [
  { id: 'repairs',       label: 'Repairs',            route: '/book-repair',          header_visible: true,  more_visible: false, footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 10,  footer_sort_order: 10, open_new_tab: false, active: true, is_cta: false },
  { id: 'controllers',   label: 'Custom Controllers', route: '/custom-controllers',   header_visible: true,  more_visible: false, footer_visible: true,  footer_group: 'gamefix', footer_group_order: 2, sort_order: 20,  footer_sort_order: 10, open_new_tab: false, active: true, is_cta: false },
  { id: 'mail-in',       label: 'Mail-In',            route: '/mail-in-repairs',      header_visible: true,  more_visible: false, footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 30,  footer_sort_order: 20, open_new_tab: false, active: true, is_cta: false },
  { id: 'support',       label: 'Support',            route: '/support',              header_visible: true,  more_visible: false, footer_visible: true,  footer_group: 'support', footer_group_order: 3, sort_order: 40,  footer_sort_order: 10, open_new_tab: false, active: true, is_cta: false },
  { id: 'book-repair',   label: 'Book Repair',        route: '/book-repair',          header_visible: true,  more_visible: false, footer_visible: false, footer_group: null,      footer_group_order: 0, sort_order: 50,  footer_sort_order: 0,  open_new_tab: false, active: true, is_cta: true  },
  { id: 'gallery',       label: 'Gallery',            route: '/gallery',              header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'gamefix', footer_group_order: 2, sort_order: 60,  footer_sort_order: 20, open_new_tab: false, active: true, is_cta: false },
  { id: 'buyback',       label: 'Buyback',            route: '/buyback',              header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'gamefix', footer_group_order: 2, sort_order: 70,  footer_sort_order: 30, open_new_tab: false, active: true, is_cta: false },
  { id: 'shop',          label: 'Shop',               route: '/shop',                 header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'gamefix', footer_group_order: 2, sort_order: 80,  footer_sort_order: 40, open_new_tab: false, active: true, is_cta: false },
  { id: 'about',         label: 'About',              route: '/about',                header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'gamefix', footer_group_order: 2, sort_order: 90,  footer_sort_order: 50, open_new_tab: false, active: true, is_cta: false },
  { id: 'upgrades',      label: 'Upgrades',           route: '/performance-upgrades', header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 100, footer_sort_order: 30, open_new_tab: false, active: true, is_cta: false },
  { id: 'retro-mods',    label: 'Retro Mods',         route: '/retro-mods',           header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 110, footer_sort_order: 40, open_new_tab: false, active: true, is_cta: false },
  { id: 'community',     label: 'Community',          route: '/community',            header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'support', footer_group_order: 3, sort_order: 120, footer_sort_order: 20, open_new_tab: false, active: true, is_cta: false },
  { id: 'blog',          label: 'Blog',               route: '/blog',                 header_visible: false, more_visible: true,  footer_visible: true,  footer_group: 'support', footer_group_order: 3, sort_order: 130, footer_sort_order: 30, open_new_tab: false, active: true, is_cta: false },
  { id: 'bulk-services', label: 'Bulk Services',      route: '/bulk-services',        header_visible: false, more_visible: false, footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 140, footer_sort_order: 50, open_new_tab: false, active: true, is_cta: false },
  { id: 'repair-db',     label: 'Repair Database',    route: '/repair-database',      header_visible: false, more_visible: false, footer_visible: true,  footer_group: 'support', footer_group_order: 3, sort_order: 150, footer_sort_order: 40, open_new_tab: false, active: true, is_cta: false },
  { id: 'phone-repair',  label: 'Phone Repair',       route: '/phone-repair',         header_visible: false, more_visible: false, footer_visible: true,  footer_group: 'repairs', footer_group_order: 1, sort_order: 160, footer_sort_order: 60, open_new_tab: false, active: true, is_cta: false },
];

// ── Cache helpers ─────────────────────────────────────────────────────────────

function readCache(): NavCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as NavCache) : null;
  } catch { return null; }
}

function writeCache(items: NavItem[]): void {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ fetchedAt: Date.now(), items })); } catch { /* unavailable */ }
}

export function invalidateNavCache(): void {
  try { localStorage.removeItem(CACHE_KEY); } catch { /* unavailable */ }
}

// ── Fetch ─────────────────────────────────────────────────────────────────────

export async function fetchNavItems(): Promise<NavItem[]> {
  const cached = readCache();
  // Treat a cached empty array as stale — it means a previous fetch returned nothing
  if (cached && cached.items.length > 0 && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.items;
  }

  try {
    const res = await fetch(`${API_BASE}/api/cms/navigation`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { items } = await res.json() as { items: NavItem[] };
    // An empty response means Supabase was unavailable — don't cache it
    if (!Array.isArray(items) || items.length === 0) throw new Error('empty');
    writeCache(items);
    return items;
  } catch {
    // Return stale cache (if it has items) rather than fallback, so admins see their changes
    if (cached && cached.items.length > 0) return cached.items;
    return FALLBACK_NAV;
  }
}

// ── Admin mutations ───────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = sessionStorage.getItem('gfs_admin_token') ?? '';
  return { 'Content-Type': 'application/json', 'x-admin-token': token };
}

export async function updateNavItem(id: string, patch: Partial<NavItem>): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/navigation/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Update failed: ${res.status}`);
  invalidateNavCache();
}

export async function bulkUpdateNavOrder(
  updates: { id: string; sort_order?: number; footer_sort_order?: number; footer_group?: string | null; footer_group_order?: number }[]
): Promise<void> {
  const res = await fetch(`${API_BASE}/api/cms/navigation/bulk`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ updates }),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error(`Bulk update failed: ${res.status}`);
  invalidateNavCache();
}

// ── Display helpers ───────────────────────────────────────────────────────────

export function activeHeaderItems(items: NavItem[]): NavItem[] {
  return items.filter(i => i.active && i.header_visible && !i.is_cta).sort((a, b) => a.sort_order - b.sort_order);
}

export function activeMoreItems(items: NavItem[]): NavItem[] {
  return items.filter(i => i.active && i.more_visible).sort((a, b) => a.sort_order - b.sort_order);
}

export function ctaItem(items: NavItem[]): NavItem | undefined {
  return items.find(i => i.active && i.is_cta);
}

export interface FooterGroup {
  key: string;
  label: string;
  order: number;
  items: NavItem[];
}

const FOOTER_GROUP_LABELS: Record<string, string> = {
  repairs: 'Repairs',
  gamefix: 'GameFix',
  support: 'Support',
};

export function footerGroups(items: NavItem[]): FooterGroup[] {
  const visible = items.filter(i => i.active && i.footer_visible && i.footer_group);
  const byGroup = new Map<string, NavItem[]>();
  for (const item of visible) {
    const g = item.footer_group!;
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(item);
  }
  const groups: FooterGroup[] = [];
  for (const [key, groupItems] of byGroup) {
    const order = Math.min(...groupItems.map(i => i.footer_group_order));
    groups.push({
      key,
      label: FOOTER_GROUP_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1),
      order,
      items: groupItems.sort((a, b) => a.footer_sort_order - b.footer_sort_order),
    });
  }
  return groups.sort((a, b) => a.order - b.order);
}
